import * as Tone from 'tone';
import MuseFlowAudio, {
  NativeChannelVolumes,
  isMuseFlowAudioPluginAvailable,
} from 'Utils/MuseFlowAudioPlugin';
import {
  DEFAULT_VOLUME_SETTINGS,
  VOLUME_CHANNELS,
  VolumeChannel,
  VolumeSettings,
  channelGain,
} from './volumeSettings';

/**
 * Where the volume settings meet the web audio. Every web source plays through
 * its channel here:
 *
 * - Tone.js sources (the piano sampler, the scheduler's metronome clicks) are
 *   connected through one gain node per channel instead of straight to the
 *   destination (`routeToChannel`).
 * - Phaser sounds take their channel's gain as their play volume, read at
 *   each play, so every sound plays at the current level without tracking
 *   each scene's sounds: the metronome clicks (`metronomeSoundConfig`) and the
 *   sound effects, the exercises' feedback clips and success sound
 *   (`soundEffectConfig`).
 * - The tutorial videos take the narration channel's gain
 *   (`applyVideoNarrationGain`).
 * - On the native app the piano, the metronome, narration and the sound
 *   effects play through the MuseFlowAudio plugin, which takes every
 *   channel's gain from `applyVolumeSettings`, where every change lands.
 */

type ChannelGains = Map<VolumeChannel, Tone.Gain>;

// Gain changes are ramped so a slider drag does not click.
const GAIN_RAMP_SECONDS = 0.03;

let currentSettings: VolumeSettings = DEFAULT_VOLUME_SETTINGS;

// One gain per channel per Tone context: the app replaces its context when the
// browser closes or interrupts it, and a node can only connect inside its own.
const gainsByContext = new Map<Tone.BaseContext, ChannelGains>();

const isClosed = (context: Tone.BaseContext): boolean => {
  const state: string | undefined = context.rawContext?.state;
  return state === 'closed';
};

const forgetClosedContexts = (): void => {
  Array.from(gainsByContext.keys())
    .filter(isClosed)
    .forEach((context) => gainsByContext.delete(context));
};

// A context's gains go as soon as it closes, not at the next call here, so a
// closed context and its nodes are not kept alive in between. Heard on the
// browser's own context: the app replaces a context with Tone's dispose(),
// which drops Tone's listeners before the context closes.
const forgetWhenClosed = (context: Tone.BaseContext): void => {
  const raw = context.rawContext;
  const dropIfClosed = () => {
    if (!isClosed(context)) return;
    gainsByContext.delete(context);
    raw.removeEventListener('statechange', dropIfClosed);
  };
  raw.addEventListener('statechange', dropIfClosed);
};

const gainsFor = (context: Tone.BaseContext): ChannelGains => {
  const existing = gainsByContext.get(context);
  if (existing) return existing;
  const created: ChannelGains = new Map();
  gainsByContext.set(context, created);
  forgetWhenClosed(context);
  return created;
};

const channelGainNode = (context: Tone.BaseContext, channel: VolumeChannel): Tone.Gain => {
  forgetClosedContexts();
  const gains = gainsFor(context);
  const existing = gains.get(channel);
  if (existing) return existing;
  const created = new Tone.Gain({ gain: channelGain(currentSettings, channel), context });
  created.connect(context.destination);
  gains.set(channel, created);
  return created;
};

/** Connects a Tone.js source to the destination through its channel's gain. */
export const routeToChannel = <Node extends Tone.ToneAudioNode>(
  node: Node,
  channel: VolumeChannel,
): Node => {
  node.connect(channelGainNode(node.context, channel));
  return node;
};

/** The play config for a Phaser sound on this channel at the current volume. */
export const channelSoundConfig = (channel: VolumeChannel): { volume: number } => ({
  volume: channelGain(currentSettings, channel),
});

/** The play config for a Phaser metronome click at the current volume. */
export const metronomeSoundConfig = (): { volume: number } => channelSoundConfig('metronome');

/** The play config for a Phaser sound effect (feedback clip, success) at the current volume. */
export const soundEffectConfig = (): { volume: number } => channelSoundConfig('sfx');

export const getAppliedVolumeSettings = (): VolumeSettings => currentSettings;

/** Every channel's gain, as the native plugin takes them. */
export const nativeChannelVolumes = (settings: VolumeSettings): NativeChannelVolumes => ({
  piano: channelGain(settings, 'piano'),
  metronome: channelGain(settings, 'metronome'),
  narration: channelGain(settings, 'narration'),
  sfx: channelGain(settings, 'sfx'),
});

// Not awaited: the plugin applies the gains on its own queue, and nothing that
// plays should wait for a volume change.
const applyToNativeAudio = (settings: VolumeSettings): void => {
  if (!isMuseFlowAudioPluginAvailable()) return;
  MuseFlowAudio.setChannelVolumes(nativeChannelVolumes(settings)).catch(() => {
    // An app build without the volume methods plays at full volume, as before.
  });
};

/** Makes these settings the ones every channel plays at, from now on. */
export const applyVolumeSettings = (settings: VolumeSettings): void => {
  currentSettings = settings;
  applyToNativeAudio(settings);
  forgetClosedContexts();
  gainsByContext.forEach((gains) =>
    VOLUME_CHANNELS.forEach((channel) => {
      const gain = gains.get(channel);
      if (!gain) return;
      try {
        gain.gain.rampTo(channelGain(settings, channel), GAIN_RAMP_SECONDS);
      } catch {
        // A context closing between the check and the ramp has nothing left to play.
      }
    }),
  );
};
